/**
 * RBAC permission catalog — keep in sync with migration 153_rbac_phase0_schema.sql.
 */

export const PERMISSION_CODES = [
  'dashboard.read',
  'analytics.read',
  'orders.read',
  'orders.write',
  'orders.prepare',
  'orders.deliver',
  'orders.cancel',
  'tabs.read',
  'tabs.write',
  'tabs.close',
  'tabs.assign_table',
  'payments.read',
  'payments.charge',
  'payments.refund',
  'menu.read',
  'menu.write',
  'inventory.read',
  'inventory.write',
  'invoices.read',
  'users.read',
  'users.write',
  'users.manage',
  'venues.read',
  'venues.write',
  'venues.connect',
  'billing.read',
  'billing.manage',
  'security.read',
  'security.write',
] as const;

export type PermissionCode = (typeof PERMISSION_CODES)[number];

export type SystemAccessProfileCode =
  | 'admin'
  | 'manager'
  | 'bar'
  | 'kitchen'
  | 'waiter'
  | 'investor'
  | 'finance';

export type SystemAccessProfile = {
  code: SystemAccessProfileCode;
  name: string;
  permissions: PermissionCode[];
};

/** System profile → permission matrix (mirrors migration 153 seed). */
export const SYSTEM_ACCESS_PROFILES: SystemAccessProfile[] = [
  {
    code: 'admin',
    name: 'Administrador',
    permissions: [
      'dashboard.read',
      'analytics.read',
      'orders.read',
      'orders.write',
      'orders.prepare',
      'orders.deliver',
      'orders.cancel',
      'tabs.read',
      'tabs.write',
      'tabs.close',
      'tabs.assign_table',
      'payments.read',
      'payments.charge',
      'payments.refund',
      'menu.read',
      'menu.write',
      'inventory.read',
      'inventory.write',
      'invoices.read',
      'users.read',
      'users.write',
      'users.manage',
      'venues.read',
      'venues.write',
      'venues.connect',
      'billing.read',
      'billing.manage',
      'security.read',
    ],
  },
  {
    code: 'manager',
    name: 'Gerente',
    permissions: [
      'dashboard.read',
      'analytics.read',
      'orders.read',
      'orders.write',
      'orders.prepare',
      'orders.deliver',
      'orders.cancel',
      'tabs.read',
      'tabs.write',
      'tabs.close',
      'tabs.assign_table',
      'payments.read',
      'payments.charge',
      'payments.refund',
      'menu.read',
      'menu.write',
      'inventory.read',
      'inventory.write',
      'invoices.read',
      'users.read',
      'users.write',
      'users.manage',
      'venues.read',
      'security.read',
    ],
  },
  {
    code: 'bar',
    name: 'Bar',
    permissions: [
      'orders.read',
      'orders.prepare',
      'orders.deliver',
      'tabs.read',
      'tabs.write',
      'tabs.close',
      'tabs.assign_table',
      'payments.read',
      'payments.charge',
      'menu.read',
    ],
  },
  {
    code: 'kitchen',
    name: 'Cozinha',
    permissions: ['orders.read', 'orders.prepare', 'menu.read'],
  },
  {
    code: 'waiter',
    name: 'Garçom',
    permissions: [
      'orders.read',
      'orders.write',
      'orders.deliver',
      'tabs.read',
      'tabs.write',
      'tabs.close',
      'tabs.assign_table',
      'payments.read',
      'payments.charge',
      'menu.read',
    ],
  },
  {
    code: 'investor',
    name: 'Investidor',
    permissions: [
      'dashboard.read',
      'analytics.read',
      'orders.read',
      'tabs.read',
      'payments.read',
      'menu.read',
      'inventory.read',
      'invoices.read',
      'venues.read',
    ],
  },
  {
    code: 'finance',
    name: 'Financeiro',
    permissions: [
      'dashboard.read',
      'analytics.read',
      'orders.read',
      'tabs.read',
      'payments.read',
      'payments.refund',
      'inventory.read',
      'invoices.read',
      'venues.read',
      'billing.read',
    ],
  },
];

export const SYSTEM_ACCESS_PROFILE_MAP = Object.fromEntries(
  SYSTEM_ACCESS_PROFILES.map((profile) => [profile.code, profile])
) as Record<SystemAccessProfileCode, SystemAccessProfile>;

/** Profiles available in admin preview (exclude admin impersonation). */
export const PREVIEWABLE_PROFILE_CODES = SYSTEM_ACCESS_PROFILES.filter(
  (profile) => profile.code !== 'admin'
).map((profile) => profile.code);

export function getSystemProfilePermissions(code: SystemAccessProfileCode): PermissionCode[] {
  return SYSTEM_ACCESS_PROFILE_MAP[code]?.permissions ?? [];
}

const LEGACY_ROLE_TO_PROFILE: Record<string, SystemAccessProfileCode> = {
  bartender: 'bar',
  cook: 'kitchen',
  staff: 'bar',
  owner: 'admin',
};

/** Maps venue_users.role string (incl. legacy labels) to a system access profile code. */
export function resolveProfileCode(role?: string | null): SystemAccessProfileCode | null {
  if (!role) return null;
  const normalized = role.toLowerCase().trim();
  const code = LEGACY_ROLE_TO_PROFILE[normalized] ?? normalized;
  return code in SYSTEM_ACCESS_PROFILE_MAP ? (code as SystemAccessProfileCode) : null;
}

export function hasPermission(
  permissions: readonly string[] | undefined,
  code: PermissionCode
): boolean {
  return !!permissions?.includes(code);
}

export function hasAnyPermission(
  permissions: readonly string[] | undefined,
  codes: PermissionCode[]
): boolean {
  if (!permissions?.length) return false;
  return codes.some((code) => permissions.includes(code));
}

export function hasAllPermissions(
  permissions: readonly string[] | undefined,
  codes: PermissionCode[]
): boolean {
  if (!codes.length) return true;
  if (!permissions?.length) return false;
  return codes.every((code) => permissions.includes(code));
}
